import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Avatar,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Card,
    CardContent,
    Alert,
    Tabs,
    Tab,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    InputAdornment,
    Stack
} from '@mui/material';
import {
    Search as SearchIcon,
    Visibility as ViewIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Person as PersonIcon,
    School as SchoolIcon,
    Email as EmailIcon,
    Psychology as PsychologyIcon,
    Assessment as AssessmentIcon,
    History as HistoryIcon,
    TrendingUp as TrendingUpIcon,
    Block as BlockIcon,
    CheckCircle as CheckCircleIcon,
    AccountBalanceWallet as WalletIcon,
    Login as LoginIcon,
    Download as DownloadIcon,
    Devices as DevicesIcon,
    Timeline as TimelineIcon
} from '@mui/icons-material';
import { format, formatDistanceToNowStrict, isValid } from 'date-fns';
import api from '../../services/api';
import { getUserProfileUrl } from '../../utils/mediaUrl';
import { canonicalizeRole } from '../../utils/role';


const safeFormat = (date, pattern = 'MMM dd, yyyy') => {
  if (!date) return 'N/A';
  const d = new Date(date);
  return isValid(d) ? format(d, pattern) : 'N/A';
};

const resolveRoleLabel = (user = {}) => user.legacyRole || user.role || 'student';

const formatRelativeDate = (date) => {
  if (!date) return 'Never';
  const d = new Date(date);
  return isValid(d) ? `${formatDistanceToNowStrict(d)} ago` : 'Never';
};

const formatProviderLabel = (provider) => {
  if (!provider) return 'N/A';
  return provider.toString().charAt(0).toUpperCase() + provider.toString().slice(1);
};

// Default parameters only cover `undefined`; API/loading state can explicitly
// provide `null`, so keep every usage selector null-safe.
const getUsageInsights = (user) => user?.usageInsights || {};

const getDownloadInsights = (user = {}) => getUsageInsights(user).appDownloads || {};

const getUnityInsights = (user) => getUsageInsights(user).unityClient || user?.clientUsage?.unity || {};

const getAuthInsights = (user) => getUsageInsights(user).auth || {
  lastLoginAt: user?.lastLoginAt || null,
  lastLoginProvider: user?.lastLoginProvider || null,
  primaryAuthProvider: user?.primaryAuthProvider || null,
  linkedProviders: user?.authProviders || []
};

const statusChip = (active, activeLabel, inactiveLabel, color = 'success') => (
  <Chip
    label={active ? activeLabel : inactiveLabel}
    color={active ? color : 'default'}
    size="small"
    variant={active ? 'filled' : 'outlined'}
  />
);

const getTokenBalance = (user = {}) => {
  const value = Number(user?.billing?.wallet?.balance || 0);
  return Number.isFinite(value) ? value : 0;
};

const formatTokenBalance = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toLocaleString() : '0';
};

const resolveRoleColor = (user = {}) => {
  const canonical = canonicalizeRole(user.role || user.legacyRole, user.accountType);

  switch (canonical) {
    case 'global_admin':
      return 'error';
    case 'university_admin':
      return 'warning';
    case 'vm_instructor':
    case 'university_instructor':
      return 'primary';
    case 'doctor':
      return 'success';
    case 'educator':
      return 'warning';
    default:
      return 'default';
  }
};

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalUsers, setTotalUsers] = useState(0);
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');

    // Dialog states
    const [selectedUser, setSelectedUser] = useState(null);
    const [userDetailDialog, setUserDetailDialog] = useState(false);
    const [roleChangeDialog, setRoleChangeDialog] = useState(false);
    const [newRole, setNewRole] = useState('');
    const [tokenTargetUser, setTokenTargetUser] = useState(null);
    const [tokenDialog, setTokenDialog] = useState(false);
    const [tokenForm, setTokenForm] = useState({ operation: 'set', amount: '', note: '' });
    const [tokenSaving, setTokenSaving] = useState(false);
    const [tokenError, setTokenError] = useState('');
    const [userHistories, setUserHistories] = useState([]);
    const [historiesLoading, setHistoriesLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const loadUsers = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/users', {
                params: {
                    page: page + 1,
                    limit: rowsPerPage,
                    search,
                    role: roleFilter,
                    sortBy,
                    sortOrder
                }
            });
            setUsers(response.data.users);
            setTotalUsers(response.data.pagination.total);
        } catch (err) {
            setError('Failed to load users');
            console.error('Error loading users:', err);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, search, roleFilter, sortBy, sortOrder]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const handleViewUser = async (user) => {
        try {
            setSelectedUser(user);
            setUserDetailDialog(true);
            setActiveTab(0);

            // Load detailed user data
            const response = await api.get(`/admin/users/${user._id}`);
            setSelectedUser(response.data);
        } catch (err) {
            setError('Failed to load user details');
            console.error('Error loading user details:', err);
        }
    };

    const handleRoleChange = (user) => {
        setSelectedUser(user);
        setNewRole(canonicalizeRole(user.role || user.legacyRole, user.accountType));
        setRoleChangeDialog(true);
    };

    const handleTokenDialogOpen = (user) => {
        setTokenTargetUser(user);
        setTokenForm({
            operation: 'set',
            amount: getTokenBalance(user).toString(),
            note: ''
        });
        setTokenError('');
        setTokenDialog(true);
    };

    const handleTokenOperationChange = (event) => {
        const operation = event.target.value;
        setTokenForm((prev) => ({
            ...prev,
            operation,
            amount: operation === 'set' && tokenTargetUser
                ? getTokenBalance(tokenTargetUser).toString()
                : ''
        }));
        setTokenError('');
    };

    const applyUpdatedUserBalance = (updatedUser) => {
        if (!updatedUser?._id) {
            return;
        }

        setUsers((prev) => prev.map((user) => (
            user._id === updatedUser._id
                ? { ...user, billing: updatedUser.billing, profilePictureUrl: updatedUser.profilePictureUrl || user.profilePictureUrl }
                : user
        )));

        setSelectedUser((prev) => {
            if (!prev) {
                return prev;
            }
            if (prev.user?._id === updatedUser._id) {
                return {
                    ...prev,
                    user: { ...prev.user, billing: updatedUser.billing, profilePictureUrl: updatedUser.profilePictureUrl || prev.user.profilePictureUrl }
                };
            }
            if (prev._id === updatedUser._id) {
                return { ...prev, billing: updatedUser.billing, profilePictureUrl: updatedUser.profilePictureUrl || prev.profilePictureUrl };
            }
            return prev;
        });

        setTokenTargetUser((prev) => (
            prev?._id === updatedUser._id
                ? { ...prev, billing: updatedUser.billing, profilePictureUrl: updatedUser.profilePictureUrl || prev.profilePictureUrl }
                : prev
        ));
    };

    const confirmTokenUpdate = async () => {
        if (!tokenTargetUser?._id) {
            return;
        }

        const amount = Number(tokenForm.amount);
        const requiresPositiveAmount = tokenForm.operation !== 'set';
        if (!Number.isInteger(amount) || amount < 0 || (requiresPositiveAmount && amount === 0)) {
            setTokenError(requiresPositiveAmount ? 'Enter a positive whole number of tokens.' : 'Enter zero or a positive whole number of tokens.');
            return;
        }

        try {
            setTokenSaving(true);
            setTokenError('');
            const response = await api.put(`/admin/users/${tokenTargetUser._id}/tokens`, {
                operation: tokenForm.operation,
                amount,
                note: tokenForm.note
            });
            applyUpdatedUserBalance(response.data.user);
            setTokenDialog(false);
            setTokenTargetUser(null);
            setTokenForm({ operation: 'set', amount: '', note: '' });
        } catch (err) {
            setTokenError(err.response?.data?.message || 'Failed to update token balance');
            console.error('Error updating token balance:', err);
        } finally {
            setTokenSaving(false);
        }
    };

    const confirmRoleChange = async () => {
        try {
            await api.put(`/admin/users/${selectedUser._id}/role`, { role: newRole });
            setRoleChangeDialog(false);
            loadUsers();
            setError('');
        } catch (err) {
            setError('Failed to update user role');
            console.error('Error updating role:', err);
        }
    };

    const handleDeleteUser = async (user) => {
        if (!window.confirm(`Are you sure you want to delete ${user.firstName} ${user.lastName}? This action cannot be undone.`)) {
            return;
        }

        try {
            await api.delete(`/admin/users/${user._id}`);
            loadUsers();
            setError('');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete user');
            console.error('Error deleting user:', err);
        }
    };

    const handleDeactivateUser = async (user) => {
        const reason = prompt('Enter reason for deactivation (optional):');
        if (reason === null) return; // User cancelled

        try {
            await api.post(`/admin/accounts/${user._id}/deactivate`, { reason });
            alert('Account deactivated successfully');
            loadUsers();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to deactivate account');
        }
    };

    const handleReactivateUser = async (user) => {
        if (!window.confirm(`Are you sure you want to reactivate ${user.firstName} ${user.lastName}?`)) {
            return;
        }

        try {
            await api.post(`/admin/accounts/${user._id}/reactivate`);
            alert('Account reactivated successfully');
            loadUsers();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reactivate account');
        }
    };

    const loadUserHistories = async (userId) => {
        try {
            setHistoriesLoading(true);
            const response = await api.get(`/admin/users/${userId}/histories`);
            setUserHistories(response.data.histories);
        } catch (err) {
            console.error('Error loading user histories:', err);
        } finally {
            setHistoriesLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        if (newValue === 1 && selectedUser) {
            const selectedUserId = selectedUser.user?._id || selectedUser._id;
            if (selectedUserId) {
                loadUserHistories(selectedUserId);
            }
        }
    };

    const formatDate = (dateString) => {
        return dateString ? safeFormat(dateString, 'MMM dd, yyyy HH:mm') : 'Never';
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'success';
        if (score >= 60) return 'warning';
        return 'error';
    };

    const selectedProfile = selectedUser?.user || selectedUser;
    const selectedUsageInsights = selectedUser?.usageInsights || getUsageInsights(selectedProfile);
    const selectedAuthInsights = selectedUsageInsights.auth || getAuthInsights(selectedProfile);
    const selectedDownloadInsights = selectedUsageInsights.appDownloads || getDownloadInsights(selectedProfile);
    const selectedUnityInsights = selectedUsageInsights.unityClient || getUnityInsights(selectedProfile);
    const recentDownloadEvents = selectedUser?.recentDownloadEvents || [];

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                User Management
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Search and Filter Controls */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            placeholder="Search users..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <FormControl fullWidth>
                            <InputLabel>Filter by Role</InputLabel>
                            <Select
                                value={roleFilter}
                                label="Filter by Role"
                                onChange={(e) => setRoleFilter(e.target.value)}
                            >
                                <MenuItem value="all">All Roles</MenuItem>
                                <MenuItem value="student">Students</MenuItem>
                                <MenuItem value="doctor">Doctors</MenuItem>
                                <MenuItem value="educator">Educators</MenuItem>
                                <MenuItem value="instructor">Instructors</MenuItem>
                                <MenuItem value="admin">Admins</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <FormControl fullWidth>
                            <InputLabel>Sort By</InputLabel>
                            <Select
                                value={`${sortBy}-${sortOrder}`}
                                label="Sort By"
                                onChange={(e) => {
                                    const [field, order] = e.target.value.split('-');
                                    setSortBy(field);
                                    setSortOrder(order);
                                }}
                            >
                                <MenuItem value="createdAt-desc">Newest First</MenuItem>
                                <MenuItem value="createdAt-asc">Oldest First</MenuItem>
                                <MenuItem value="firstName-asc">Name A-Z</MenuItem>
                                <MenuItem value="firstName-desc">Name Z-A</MenuItem>
                                <MenuItem value="email-asc">Email A-Z</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* Users Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Credits</TableCell>
                            <TableCell>Last Login</TableCell>
                            <TableCell>App Download</TableCell>
                            <TableCell>Unity Client</TableCell>
                            <TableCell>Histories</TableCell>
                            <TableCell>Last History</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center">
                                    No users found
                                </TableCell>
                            </TableRow>
                        ) : (
                            users.map((user) => {
                                const auth = getAuthInsights(user);
                                const downloads = getDownloadInsights(user);
                                const unity = getUnityInsights(user);
                                const hasUnitySignal = Boolean(unity.hasSignedIn || unity.lastSeenAt);

                                return (
                                <TableRow
                                    key={user._id}
                                    hover
                                    onClick={() => handleViewUser(user)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Avatar
                                                src={getUserProfileUrl(user.profilePicture)}
                                                sx={{ mr: 2 }}
                                            >
                                                {user.firstName?.charAt(0)}
                                            </Avatar>
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="subtitle2">
                                                        {user.firstName} {user.lastName}
                                                    </Typography>
                                                    {!user.isActive && (
                                                        <Chip
                                                            label="Deactivated"
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Joined {safeFormat(user.createdAt, 'MMM yyyy')}
                                                </Typography>
                                                {!user.isActive && user.deactivatedAt && (
                                                    <Typography variant="caption" color="error.main" display="block">
                                                        Deactivated {safeFormat(user.deactivatedAt, 'MMM dd, yyyy')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{user.email}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {user.institution || user.accountType || 'No institution'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={resolveRoleLabel(user)}
                                            color={resolveRoleColor(user)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <WalletIcon fontSize="small" color="action" />
                                            <Typography variant="body2" fontWeight={600}>
                                                {formatTokenBalance(getTokenBalance(user))}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatRelativeDate(auth.lastLoginAt)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatProviderLabel(auth.lastLoginProvider || auth.primaryAuthProvider)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack spacing={0.5} alignItems="flex-start">
                                            {statusChip(downloads.hasDownloadSignal, 'Download seen', 'No signal', 'info')}
                                            <Typography variant="caption" color="text.secondary">
                                                {downloads.lastDownloadAt ? formatRelativeDate(downloads.lastDownloadAt) : `${downloads.totalEvents || 0} events`}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Stack spacing={0.5} alignItems="flex-start">
                                            {statusChip(hasUnitySignal, 'Signed in', 'No app login', 'success')}
                                            <Typography variant="caption" color="text.secondary">
                                                {unity.lastSeenAt ? formatRelativeDate(unity.lastSeenAt) : `${unity.bootstrapCount || 0} sessions`}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {user.statistics.totalHistories} histories
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Avg: {user.statistics.averageScore}%
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatDate(user.statistics.lastActivity)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <IconButton
                                                size="small"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleViewUser(user);
                                                }}
                                                title="View Details"
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                            
                                            <IconButton
                                                size="small"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleRoleChange(user);
                                                }}
                                                title="Change Role"
                                            >
                                                <EditIcon />
                                            </IconButton>

                                            <IconButton
                                                size="small"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleTokenDialogOpen(user);
                                                }}
                                                title="Edit Tokens"
                                                sx={{ color: 'info.main' }}
                                            >
                                                <WalletIcon />
                                            </IconButton>
                                            
                                            {/* NEW ACCOUNT STATUS CONTROLS */}
                                            {user.isActive ? (
                                                <IconButton
                                                    size="small"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleDeactivateUser(user);
                                                    }}
                                                    title="Deactivate Account"
                                                    sx={{ color: 'warning.main' }}
                                                >
                                                    <BlockIcon />
                                                </IconButton>
                                            ) : (
                                                <IconButton
                                                    size="small"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleReactivateUser(user);
                                                    }}
                                                    title="Reactivate Account"
                                                    sx={{ color: 'success.main' }}
                                                >
                                                    <CheckCircleIcon />
                                                </IconButton>
                                            )}
                                            
                                            <IconButton
                                                size="small"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleDeleteUser(user);
                                                }}
                                                title="Delete User"
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalUsers}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                />
            </TableContainer>

            {/* User Detail Dialog */}
            <Dialog open={userDetailDialog} onClose={() => setUserDetailDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>User Details</DialogTitle>
                <DialogContent>
                    {selectedUser && (
                        <Box>
                            <Tabs value={activeTab} onChange={handleTabChange}>
                                <Tab label="Profile" />
                                <Tab label="History" />
                                <Tab label="Statistics" />
                                <Tab label="Usage" />
                            </Tabs>

                            {/* Profile Tab */}
                            {activeTab === 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Grid container spacing={3}>
                                        <Grid size={{ xs: 12, md: 4 }}>
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Avatar
                                                    src={getUserProfileUrl(selectedProfile?.profilePicture)}
                                                    sx={{ width: 100, height: 100, mx: 'auto', mb: 2 }}
                                                >
                                                    {selectedProfile?.firstName?.charAt(0)}
                                                </Avatar>
                                                <Typography variant="h6">
                                                    {selectedProfile?.firstName} {selectedProfile?.lastName}
                                                </Typography>
                                                <Chip
                                                    label={resolveRoleLabel(selectedProfile)}
                                                    color={resolveRoleColor(selectedProfile)}
                                                    sx={{ mt: 1 }}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 8 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
                                                <Typography>{selectedProfile?.email}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <SchoolIcon sx={{ mr: 1, color: 'action.active' }} />
                                                <Typography>{selectedProfile?.institution || selectedProfile?.accountType || 'No institution'}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <WalletIcon sx={{ mr: 1, color: 'action.active' }} />
                                                <Typography>
                                                    Credits: {formatTokenBalance(getTokenBalance(selectedProfile))}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <PersonIcon sx={{ mr: 1, color: 'action.active' }} />
                                                <Typography>
                                                    Joined {safeFormat(selectedProfile?.createdAt, 'MMMM dd, yyyy')}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <LoginIcon sx={{ mr: 1, color: 'action.active' }} />
                                                <Typography>
                                                    Last login: {safeFormat(selectedAuthInsights.lastLoginAt, 'MMM dd, yyyy HH:mm')} ({formatProviderLabel(selectedAuthInsights.lastLoginProvider || selectedAuthInsights.primaryAuthProvider)})
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <DownloadIcon sx={{ mr: 1, color: 'action.active' }} />
                                                <Typography>
                                                    App download: {selectedDownloadInsights.hasDownloadSignal ? safeFormat(selectedDownloadInsights.lastDownloadAt, 'MMM dd, yyyy HH:mm') : 'No download signal'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <DevicesIcon sx={{ mr: 1, color: 'action.active' }} />
                                                <Typography>
                                                    Unity client: {selectedUnityInsights.lastSeenAt ? safeFormat(selectedUnityInsights.lastSeenAt, 'MMM dd, yyyy HH:mm') : 'No Unity sign-in signal'}
                                                </Typography>
                                            </Box>
                                            {selectedUser.statistics?.lastActivity && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <HistoryIcon sx={{ mr: 1, color: 'action.active' }} />
                                                    <Typography>
                                                        Last activity: {safeFormat(selectedUser.statistics.lastActivity, 'MMM dd, yyyy HH:mm')}
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Button
                                                size="small"
                                                startIcon={<WalletIcon />}
                                                onClick={() => handleTokenDialogOpen(selectedProfile)}
                                                disabled={!selectedProfile}
                                                sx={{ mt: 2 }}
                                            >
                                                Edit Tokens
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* History Tab */}
                            {activeTab === 1 && (
                                <Box sx={{ mt: 2 }}>
                                    {historiesLoading ? (
                                        <Box sx={{ textAlign: 'center', py: 4 }}>
                                            <CircularProgress />
                                        </Box>
                                    ) : userHistories.length === 0 ? (
                                        <Typography variant="body1" color="text.secondary" align="center">
                                            No completed histories
                                        </Typography>
                                    ) : (
                                        <List>
                                            {userHistories.map((history) => (
                                                <ListItem key={history._id} divider>
                                                    <ListItemText
                                                        primary={history.title}
                                                        secondary={`Completed ${formatDate(history.completedAt)} - Duration: ${history.duration} min`}
                                                    />
                                                    <Chip
                                                        label={`${history.overallPercentage}%`}
                                                        color={getScoreColor(history.overallPercentage)}
                                                        size="small"
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    )}
                                </Box>
                            )}

                            {/* Statistics Tab */}
                            {activeTab === 2 && selectedUser.statistics && (
                                <Box sx={{ mt: 2 }}>
                                    <Grid container spacing={3}>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Card>
                                                <CardContent sx={{ textAlign: 'center' }}>
                                                    <HistoryIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                                                    <Typography variant="h4">
                                                        {selectedUser.statistics.totalHistories}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Total Histories
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Card>
                                                <CardContent sx={{ textAlign: 'center' }}>
                                                    <AssessmentIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                                                    <Typography variant="h4">
                                                        {selectedUser.statistics.averageScore}%
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Average Score
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Card>
                                                <CardContent sx={{ textAlign: 'center' }}>
                                                    <TrendingUpIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                                                    <Typography variant="h4">
                                                        {selectedUser.statistics.highestScore}%
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Highest Score
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Card>
                                                <CardContent sx={{ textAlign: 'center' }}>
                                                    <PsychologyIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                                                    <Typography variant="h4">
                                                        {selectedUser.statistics.categoryAverages?.historyQuestions || 0}%
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        History Questions
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>

                                    {selectedUser.statistics.categoryAverages && (
                                        <Box sx={{ mt: 3 }}>
                                            <Typography variant="h6" gutterBottom>
                                                Category Performance
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="body2">
                                                            Flow Assessment: {selectedUser.statistics.categoryAverages.flow}%
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="body2">
                                                            Examiner Questions: {selectedUser.statistics.categoryAverages.examinerQuestions}%
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="body2">
                                                            Empathy: {selectedUser.statistics.categoryAverages.empathy}%
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {activeTab === 3 && (
                                <Box sx={{ mt: 2 }}>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Card>
                                                <CardContent sx={{ textAlign: 'center' }}>
                                                    <LoginIcon sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
                                                    <Typography variant="h6">
                                                        {formatRelativeDate(selectedAuthInsights.lastLoginAt)}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Website login
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Card>
                                                <CardContent sx={{ textAlign: 'center' }}>
                                                    <DownloadIcon sx={{ fontSize: 36, color: 'info.main', mb: 1 }} />
                                                    <Typography variant="h6">
                                                        {selectedDownloadInsights.hasDownloadSignal ? 'Seen' : 'No signal'}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        App download
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Card>
                                                <CardContent sx={{ textAlign: 'center' }}>
                                                    <DevicesIcon sx={{ fontSize: 36, color: 'success.main', mb: 1 }} />
                                                    <Typography variant="h6">
                                                        {selectedUnityInsights.lastSeenAt ? 'Signed in' : 'No signal'}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Unity client
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Card>
                                                <CardContent sx={{ textAlign: 'center' }}>
                                                    <TimelineIcon sx={{ fontSize: 36, color: 'warning.main', mb: 1 }} />
                                                    <Typography variant="h6">
                                                        {selectedUser.statistics?.totalHistories || 0}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Completed histories
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={3} sx={{ mt: 1 }}>
                                        <Grid size={{ xs: 12, md: 4 }}>
                                            <Typography variant="h6" gutterBottom>Authentication</Typography>
                                            <List dense>
                                                <ListItem>
                                                    <ListItemText primary="Primary provider" secondary={formatProviderLabel(selectedAuthInsights.primaryAuthProvider)} />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText primary="Last login provider" secondary={formatProviderLabel(selectedAuthInsights.lastLoginProvider)} />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText primary="Last login time" secondary={safeFormat(selectedAuthInsights.lastLoginAt, 'MMM dd, yyyy HH:mm')} />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText
                                                        primary="Linked providers"
                                                        secondary={(selectedAuthInsights.linkedProviders || []).length
                                                            ? selectedAuthInsights.linkedProviders.map((provider) => formatProviderLabel(provider.provider)).join(', ')
                                                            : 'None'}
                                                    />
                                                </ListItem>
                                            </List>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 4 }}>
                                            <Typography variant="h6" gutterBottom>App Download</Typography>
                                            <List dense>
                                                <ListItem>
                                                    <ListItemText primary="Download signal" secondary={selectedDownloadInsights.hasDownloadSignal ? 'Yes' : 'No'} />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText primary="First seen" secondary={safeFormat(selectedDownloadInsights.firstDownloadAt, 'MMM dd, yyyy HH:mm')} />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText primary="Last seen" secondary={safeFormat(selectedDownloadInsights.lastDownloadAt, 'MMM dd, yyyy HH:mm')} />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText
                                                        primary="Events"
                                                        secondary={`${selectedDownloadInsights.downloadClickCount || 0} clicks, ${selectedDownloadInsights.tokenIssuedCount || 0} tokens, ${selectedDownloadInsights.successfulDownloadCount || 0} completed downloads`}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText
                                                        primary="Platforms"
                                                        secondary={(selectedDownloadInsights.platforms || []).length ? selectedDownloadInsights.platforms.join(', ') : 'Unknown'}
                                                    />
                                                </ListItem>
                                            </List>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 4 }}>
                                            <Typography variant="h6" gutterBottom>Unity Client</Typography>
                                            <List dense>
                                                <ListItem>
                                                    <ListItemText primary="First app sign-in" secondary={safeFormat(selectedUnityInsights.firstSeenAt, 'MMM dd, yyyy HH:mm')} />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText primary="Last app sign-in" secondary={safeFormat(selectedUnityInsights.lastSeenAt, 'MMM dd, yyyy HH:mm')} />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText
                                                        primary="Session activity"
                                                        secondary={`${selectedUnityInsights.bootstrapCount || 0} bootstraps, ${selectedUnityInsights.realtimeTokenCount || 0} realtime sessions`}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText
                                                        primary="Clinical activity"
                                                        secondary={`${selectedUnityInsights.transcriptionCount || 0} transcriptions, ${selectedUnityInsights.markedHistorySubmissionCount || 0} marking submissions`}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText
                                                        primary="Last client"
                                                        secondary={[
                                                            selectedUnityInsights.lastPlatform,
                                                            selectedUnityInsights.lastAppVersion,
                                                            selectedUnityInsights.lastUnityVersion
                                                        ].filter(Boolean).join(' / ') || 'Unknown'}
                                                    />
                                                </ListItem>
                                            </List>
                                        </Grid>
                                    </Grid>

                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="h6" gutterBottom>Recent Download Events</Typography>
                                        {recentDownloadEvents.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                No download events recorded for this user.
                                            </Typography>
                                        ) : (
                                            <List dense>
                                                {recentDownloadEvents.map((event) => (
                                                    <ListItem key={event.id} divider>
                                                        <ListItemText
                                                            primary={`${event.eventType.replace(/_/g, ' ')} - ${event.build?.platform || event.metadata?.platform || 'unknown platform'}`}
                                                            secondary={`${safeFormat(event.createdAt, 'MMM dd, yyyy HH:mm')} - ${event.build?.filename || event.build?.version || 'Unknown build'}`}
                                                        />
                                                        <Chip
                                                            label={event.status || 'info'}
                                                            color={event.status === 'success' ? 'success' : event.status === 'failure' ? 'error' : 'default'}
                                                            size="small"
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUserDetailDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Token Balance Dialog */}
            <Dialog open={tokenDialog} onClose={() => !tokenSaving && setTokenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Adjust Tokens</DialogTitle>
                <DialogContent>
                    {tokenTargetUser && (
                        <Box sx={{ pt: 1 }}>
                            <Typography gutterBottom>
                                {tokenTargetUser.firstName} {tokenTargetUser.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Current credits: {formatTokenBalance(getTokenBalance(tokenTargetUser))}
                            </Typography>
                            {tokenError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {tokenError}
                                </Alert>
                            )}
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Adjustment</InputLabel>
                                <Select
                                    value={tokenForm.operation}
                                    label="Adjustment"
                                    onChange={handleTokenOperationChange}
                                    disabled={tokenSaving}
                                >
                                    <MenuItem value="set">Set balance</MenuItem>
                                    <MenuItem value="add">Add tokens</MenuItem>
                                    <MenuItem value="deduct">Deduct tokens</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                type="number"
                                label={tokenForm.operation === 'set' ? 'New token balance' : 'Token amount'}
                                value={tokenForm.amount}
                                onChange={(e) => {
                                    setTokenForm((prev) => ({ ...prev, amount: e.target.value }));
                                    setTokenError('');
                                }}
                                inputProps={{ min: 0, step: 1 }}
                                disabled={tokenSaving}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Admin note"
                                value={tokenForm.note}
                                onChange={(e) => setTokenForm((prev) => ({ ...prev, note: e.target.value }))}
                                disabled={tokenSaving}
                                multiline
                                minRows={2}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTokenDialog(false)} disabled={tokenSaving}>Cancel</Button>
                    <Button
                        onClick={confirmTokenUpdate}
                        variant="contained"
                        disabled={tokenSaving || !tokenTargetUser}
                    >
                        {tokenSaving ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Role Change Dialog */}
            <Dialog open={roleChangeDialog} onClose={() => setRoleChangeDialog(false)}>
                <DialogTitle>Change User Role</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Change role for {selectedUser?.firstName} {selectedUser?.lastName}
                    </Typography>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>New Role</InputLabel>
                        <Select
                            value={newRole}
                            label="New Role"
                            onChange={(e) => setNewRole(e.target.value)}
                        >
                            <MenuItem value="student">Student</MenuItem>
                            <MenuItem value="doctor">Doctor</MenuItem>
                            <MenuItem value="educator">Educator</MenuItem>
                            <MenuItem value="instructor">Instructor</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRoleChangeDialog(false)}>Cancel</Button>
                    <Button onClick={confirmRoleChange} variant="contained">
                        Update Role
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserManagement;
